import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { usePageMeta } from '../hooks/usePageMeta';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Mail,
  Phone,
  Clock,
  MessageCircle,
  Send,
  CheckCircle2,
  AlertCircle,
  CalendarCheck,
  User,
  Building2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactPage = () => {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();

  usePageMeta('contact.title', 'seo.contactDescription');

  const initialAudience =
    searchParams.get('audience') === 'corporate' ? 'corporate' : 'private';

  const [audience, setAudience] = useState(initialAudience);
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
    website: '', // honeypot — must stay empty
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ kind: 'idle', message: '' }); // idle | submitting | success | error

  useEffect(() => {
    const a = searchParams.get('audience');
    if (a === 'corporate' || a === 'private') setAudience(a);
  }, [searchParams]);

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    if (errors[field]) setErrors({ ...errors, [field]: '' });
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'errorName';
    if (!form.email.trim()) {
      next.email = 'errorEmailRequired';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      next.email = 'errorEmailInvalid';
    }
    if (form.message.trim().length < 10) {
      next.message = 'errorMessage';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus({ kind: 'submitting', message: '' });
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim() || null,
        message: form.message.trim(),
        audience,
        company: form.company.trim() || null,
        website: form.website,
        language,
      };
      await axios.post(`${API}/contact`, payload);
      setStatus({ kind: 'success', message: t('contactForm.successBody') });
      setForm({ name: '', email: '', company: '', subject: '', message: '', website: '' });
    } catch (err) {
      const msg =
        err?.response?.status === 429
          ? t('contactForm.errorRateLimit')
          : t('contactForm.errorGeneric');
      setStatus({ kind: 'error', message: msg });
    }
  };

  const isCorporate = audience === 'corporate';
  const submitting = status.kind === 'submitting';

  return (
    <div className="min-h-screen" data-testid="contact-page">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-sea-blue/5 via-white to-bronze/5 py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('contact.title')}
            </h1>
            <p className="text-2xl text-gray-700 font-semibold">{t('contact.subtitle')}</p>
            <p className="text-xl text-gray-600">{t('contact.tagline')}</p>
          </div>
        </div>
      </section>

      {/* We're Here to Guide You */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {t('contact.guideTitle')}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">{t('contact.guide1')}</p>
            <p className="text-lg text-gray-700 leading-relaxed mt-4">{t('contact.guide2')}</p>
          </div>
        </div>
      </section>

      {/* Form + Info */}
      <section className="py-16 bg-gradient-to-br from-bronze/5 via-white to-sea-blue/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-8">
            {/* FORM (3/5) */}
            <Card
              className="lg:col-span-3 border-2 border-sea-blue/20 shadow-lg"
              data-testid="contact-form-card"
            >
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900">
                  {t('contactForm.title')}
                </CardTitle>
                <p className="text-gray-600 text-sm mt-1">{t('contactForm.subtitle')}</p>
              </CardHeader>
              <CardContent>
                {status.kind === 'success' ? (
                  <div
                    className="rounded-lg border-2 border-green-200 bg-green-50 p-6 text-center"
                    data-testid="contact-success"
                  >
                    <div className="mx-auto w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-4">
                      <CheckCircle2 className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {t('contactForm.successTitle')}
                    </h3>
                    <p className="text-gray-700 mb-4">{status.message}</p>
                    <Button
                      onClick={() => setStatus({ kind: 'idle', message: '' })}
                      variant="outline"
                      className="border-sea-blue text-sea-blue hover:bg-sea-blue hover:text-white"
                      data-testid="contact-success-reset"
                    >
                      {t('contactForm.sendAnother')}
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} noValidate className="space-y-5">
                    {/* Audience toggle */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-2 block">
                        {t('contactForm.audienceLabel')}
                      </Label>
                      <div className="grid grid-cols-2 gap-3" role="tablist">
                        <button
                          type="button"
                          onClick={() => setAudience('private')}
                          className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border-2 transition-all text-sm font-semibold ${
                            !isCorporate
                              ? 'border-sea-blue bg-sea-blue text-white'
                              : 'border-gray-200 text-gray-700 hover:border-sea-blue/50 hover:text-sea-blue'
                          }`}
                          data-testid="contact-audience-private"
                        >
                          <User className="h-4 w-4" />
                          {t('contactForm.audiencePrivate')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setAudience('corporate')}
                          className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border-2 transition-all text-sm font-semibold ${
                            isCorporate
                              ? 'border-bronze bg-bronze text-white'
                              : 'border-gray-200 text-gray-700 hover:border-bronze/50 hover:text-bronze'
                          }`}
                          data-testid="contact-audience-corporate"
                        >
                          <Building2 className="h-4 w-4" />
                          {t('contactForm.audienceCorporate')}
                        </button>
                      </div>
                    </div>

                    {/* Honeypot — visually hidden */}
                    <div
                      style={{
                        position: 'absolute',
                        left: '-10000px',
                        top: 'auto',
                        width: '1px',
                        height: '1px',
                        overflow: 'hidden',
                      }}
                      aria-hidden="true"
                    >
                      <label>
                        Website (leave empty):{' '}
                        <input
                          type="text"
                          name="website"
                          tabIndex={-1}
                          autoComplete="off"
                          value={form.website}
                          onChange={handleChange('website')}
                        />
                      </label>
                    </div>

                    {/* Name */}
                    <div>
                      <Label htmlFor="contact-name" className="text-sm font-medium text-gray-700">
                        {t('contactForm.nameLabel')} <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="contact-name"
                        type="text"
                        autoComplete="name"
                        value={form.name}
                        onChange={handleChange('name')}
                        placeholder={t('contactForm.namePlaceholder')}
                        className="mt-1"
                        data-testid="contact-input-name"
                      />
                      {errors.name && (
                        <p className="text-sm text-red-600 mt-1" data-testid="contact-error-name">
                          {t(`contactForm.${errors.name}`)}
                        </p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <Label htmlFor="contact-email" className="text-sm font-medium text-gray-700">
                        {t('contactForm.emailLabel')} <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="contact-email"
                        type="email"
                        autoComplete="email"
                        value={form.email}
                        onChange={handleChange('email')}
                        placeholder={t('contactForm.emailPlaceholder')}
                        className="mt-1"
                        data-testid="contact-input-email"
                      />
                      {errors.email && (
                        <p className="text-sm text-red-600 mt-1" data-testid="contact-error-email">
                          {errors.email}
                        </p>
                      )}
                    </div>

                    {/* Company (corporate only) */}
                    {isCorporate && (
                      <div>
                        <Label
                          htmlFor="contact-company"
                          className="text-sm font-medium text-gray-700"
                        >
                          {t('contactForm.companyLabel')}
                        </Label>
                        <Input
                          id="contact-company"
                          type="text"
                          autoComplete="organization"
                          value={form.company}
                          onChange={handleChange('company')}
                          placeholder={t('contactForm.companyPlaceholder')}
                          className="mt-1"
                          data-testid="contact-input-company"
                        />
                      </div>
                    )}

                    {/* Subject */}
                    <div>
                      <Label
                        htmlFor="contact-subject"
                        className="text-sm font-medium text-gray-700"
                      >
                        {t('contactForm.subjectLabel')}
                      </Label>
                      <Input
                        id="contact-subject"
                        type="text"
                        value={form.subject}
                        onChange={handleChange('subject')}
                        placeholder={t('contactForm.subjectPlaceholder')}
                        className="mt-1"
                        data-testid="contact-input-subject"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <Label
                        htmlFor="contact-message"
                        className="text-sm font-medium text-gray-700"
                      >
                        {t('contactForm.messageLabel')} <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="contact-message"
                        rows={6}
                        value={form.message}
                        onChange={handleChange('message')}
                        placeholder={t('contactForm.messagePlaceholder')}
                        className="mt-1 resize-y"
                        data-testid="contact-input-message"
                      />
                      {errors.message && (
                        <p
                          className="text-sm text-red-600 mt-1"
                          data-testid="contact-error-message"
                        >
                          {t(`contactForm.${errors.message}`)}
                        </p>
                      )}
                    </div>

                    {/* Submit error */}
                    {status.kind === 'error' && (
                      <div
                        className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700"
                        data-testid="contact-error-banner"
                      >
                        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>{status.message}</span>
                      </div>
                    )}

                    <p className="text-xs text-gray-500">{t('contactForm.privacyNote')}</p>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={submitting}
                      className="w-full bg-sea-blue hover:bg-sea-blue/90 text-white disabled:opacity-60"
                      data-testid="contact-submit-btn"
                    >
                      {submitting ? (
                        <>
                          <span className="h-4 w-4 mr-2 inline-block rounded-full border-2 border-white border-t-transparent animate-spin" />
                          {t('contactForm.sending')}
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-5 w-5" />
                          {t('contactForm.submit')}
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>

            {/* INFO PANEL (2/5) */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="border-2 border-sea-blue/20">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-sea-blue/10 flex items-center justify-center">
                      <Mail className="h-5 w-5 text-sea-blue" />
                    </div>
                    {t('contact.emailTitle')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <a
                    href="mailto:hello@nextstepdenmark.com"
                    className="text-base text-sea-blue hover:underline break-all"
                  >
                    hello@nextstepdenmark.com
                  </a>
                </CardContent>
              </Card>

              <Card className="border-2 border-sea-blue/20">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-sea-blue/10 flex items-center justify-center">
                      <Phone className="h-5 w-5 text-sea-blue" />
                    </div>
                    {t('contact.phoneTitle')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <a
                    href="tel:+4550912123"
                    className="text-base text-sea-blue hover:underline block mb-3"
                  >
                    +45 50 91 21 23
                  </a>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p className="flex items-start gap-2">
                      <Clock className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      {t('contact.phoneAvailability')}
                    </p>
                    <p className="flex items-start gap-2">
                      <MessageCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      {t('contact.whatsappNote')}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-bronze/30 bg-gradient-to-br from-bronze/5 to-transparent">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-bronze/15 flex items-center justify-center">
                      <CalendarCheck className="h-5 w-5 text-bronze" />
                    </div>
                    <h3 className="font-bold text-gray-900">
                      {t('contactForm.bookInsteadTitle')}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-700 mb-4">
                    {t('contactForm.bookInsteadBody')}
                  </p>
                  <Link to={isCorporate ? '/booking?type=corporate' : '/booking'}>
                    <Button
                      variant="outline"
                      className="w-full border-bronze text-bronze hover:bg-bronze hover:text-white"
                      data-testid="contact-book-instead"
                    >
                      <CalendarCheck className="mr-2 h-4 w-4" />
                      {t('contactForm.bookInsteadCta')}
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              <p className="text-sm text-gray-600 italic text-center">{t('contact.replyTime')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* What Happens */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {t('contact.whatHappensTitle')}
            </h2>
            <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
              <p>{t('contact.whatHappens1')}</p>
              <p>{t('contact.whatHappens2')}</p>
              <p className="font-semibold text-gray-900">{t('contact.whatHappens3')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Help */}
      <section className="py-16 bg-gradient-to-br from-sea-blue/5 via-white to-bronze/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {t('contact.whoWeHelpTitle')}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">{t('contact.whoWeHelp')}</p>
          </div>
        </div>
      </section>
    </div>
  );
};
